import { supabase } from '../lib/supabase';

/**
 * Payment Methods Service
 * Handles all payment method related operations
 */

export const paymentMethodsService = {
  /**
   * Get all payment methods for current user with expiration status
   */
  async getPaymentMethods() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use the database function that includes expiration calculations
      const { data, error } = await supabase?.rpc('get_user_payment_methods', { user_uuid: user?.id });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw error;
    }
  },

  /**
   * Add a new payment method
   */
  async addPaymentMethod(paymentMethodData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.from('payment_methods')?.insert([{
          user_id: user?.id,
          ...paymentMethodData
        }])?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error adding payment method:', error);
      return { data: null, error };
    }
  },

  /**
   * Update an existing payment method
   */
  async updatePaymentMethod(paymentMethodId, updates) {
    try {
      const { data, error } = await supabase?.from('payment_methods')?.update(updates)?.eq('id', paymentMethodId)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating payment method:', error);
      return { data: null, error };
    }
  },

  /**
   * Set a payment method as default
   */
  async setDefaultPaymentMethod(paymentMethodId) {
    try {
      const { data, error } = await supabase?.from('payment_methods')?.update({ is_default: true })?.eq('id', paymentMethodId)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error setting default payment method:', error);
      return { data: null, error };
    }
  },

  /**
   * Delete a payment method
   */
  async deletePaymentMethod(paymentMethodId) {
    try {
      const { error } = await supabase?.from('payment_methods')?.delete()?.eq('id', paymentMethodId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Error deleting payment method:', error);
      return { error };
    }
  },

  /**
   * Get expiring payment methods (expire within 30 days)
   */
  async getExpiringPaymentMethods() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use the database function for expiring cards
      const { data, error } = await supabase?.rpc('get_expiring_payment_methods', { user_uuid: user?.id });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching expiring payment methods:', error);
      throw error;
    }
  }
};